import Loader from '@components/loader/loader';
import React, { useEffect, useState, useRef } from 'react';
import Typography from '@components/typography';
import { v4 as uuid4 } from 'uuid';
import { Tick } from '@assets/svg/tick';
import OptionsList from '../option/optionslist';
import '@styles/customise/colourpicker.scss';

import { AvailableOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/availableoption';
import { VehicleColourOption } from 'interface';
import { useSelectedOptions } from '@hooks/useselectedoptions';
import { getMatchedPrices } from '../option/helper';

interface ColourPickerProps {
  coloursPrices: AvailableOption[];
  colourList: VehicleColourOption[];
  onClick: (selectedInfo: {
    url: string | undefined | null;
    description: string | undefined | null;
  }) => void;
  isLoading?: boolean;
}

interface VehicleColourOptionWithPrice extends VehicleColourOption {
  price: string;
  monthlyRentalCost: string;
  index: number;
  title: string;
}

const ColourPicker: React.FC<ColourPickerProps> = ({
  colourList,
  coloursPrices,
  onClick,
  isLoading,
}) => {
  const ref = useRef<HTMLUListElement>(null);
  const { selectOption } = useSelectedOptions();

  const [selectedImage, setSelectedImage] =
    useState<VehicleColourOptionWithPrice>();

  const [vehicleColours, setVehicleColours] = useState<
    VehicleColourOptionWithPrice[]
  >([]);

  const whiteColour = '0xFFFFFF';

  const formatPrice = (value: string) => Number(value?.replace('£', ''));

  ///Check that the lowest priced colour is first even if it doesnt have an image
  const sortOptions = (options: VehicleColourOptionWithPrice[]) => {
    if (options.length === 0 && options[0].hasOwnProperty('price')) {
      return [];
    }

    const rtnVal: VehicleColourOptionWithPrice[] = [];
    const withImage = options.filter((f) => f.url);
    const withoutImage = options.filter((f) => !f.url);

    if (
      withImage.length > 0 &&
      withoutImage.length > 0 &&
      formatPrice(withImage[0].price) > formatPrice(withoutImage[0].price)
    ) {
      rtnVal.push(withoutImage[0]);
      withImage.map((m) => rtnVal.push(m));
      withoutImage.map((m, i) => {
        if (i != 0) {
          rtnVal.push(m);
        }
      });
    } else {
      withImage.map((m) => rtnVal.push(m));
      withoutImage.map((m) => rtnVal.push(m));
    }

    rtnVal.forEach((e, index) => {
      e.index = index;
    });

    return rtnVal;
  };

  const setDefaultImage = (orderedColours: VehicleColourOptionWithPrice[]) => {
    if (orderedColours.length > 0) {
      onClick({
        url: orderedColours[0].url,
        description: orderedColours[0].description,
      });
    }
  };

  useEffect(() => {
    if (colourList && colourList.length > 0) {
      // Match prices and colours by description - title
      let vehicleColoursWithPrices = getMatchedPrices(
        coloursPrices,
        colourList
      );
      const selectedOption = vehicleColoursWithPrices.find((f) => f.checked);

      setSelectedImage(selectedOption);

      const orderedColours = sortOptions(vehicleColoursWithPrices);

      setVehicleColours(orderedColours as VehicleColourOptionWithPrice[]);

      if (selectedOption) {
        onClick({
          url: selectedOption?.url,
          description: selectedOption?.description ?? selectedOption?.title,
        });
      } else {
        setDefaultImage(orderedColours);
      }
    }
  }, [isLoading, colourList.length, coloursPrices.length]);

  const handleMouseLeave = () => {
    if (selectedImage) {
      onClick({
        url: selectedImage.url,
        description: selectedImage.description ?? selectedImage.title,
      });
    }
  };

  const handleMouseClickAndEnter = (
    item: VehicleColourOptionWithPrice,
    isHover: boolean = true
  ) => {
    onClick({ url: item.url, description: item.description ?? item.title });

    if (!isHover) {
      setSelectedImage(item);
      selectOption(item.id);
    }
  };

  const ColorPickerItem: React.FC<VehicleColourOptionWithPrice> = (item) => {
    const hexCode = item.hexCode
      ? `#${item.hexCode.slice(2)}`
      : 'var(--colour-white)';

    return (
      <li
        className="colour-picker__item"
        data-cy={`colour-picker__item-${item.index}`}
        data-url={item.url}
        data-description={item.description ?? item.title}
        data-testid={`colour-picker__item-${item.index}`}
        onClick={() => handleMouseClickAndEnter(item, false)}
        onMouseEnter={() => handleMouseClickAndEnter(item)}
        onMouseLeave={() => handleMouseLeave()}
      >
        <button
          data-testid={`colour-picker__item-colour-${item.index}`}
          data-cy={`colour-picker__item-colour-${item.index}`}
          data-url={item.url}
          data-description={item.description}
          title={item.description}
          type="button"
          style={{
            background: hexCode,
            ...(item.hexCode && item.hexCode !== whiteColour
              ? {}
              : {
                  outline: 'var(--config-border-alternative-colour) solid 1px',
                }),
          }}
          className="colour-picker__item-colour"
        >
          {selectedImage?.title === item.title && (
            <Tick
              className="colour-picker__item-colour-tick"
              data-cy="colour-picker__item-colour-tick"
              data-testid="colour-picker__item-colour-tick"
            />
          )}
        </button>
        <Typography
          variant="body1"
          size="sm"
          weight="semi-bold"
          data-cy={`colour-picker__item-price-${item.index}`}
          data-testid={`colour-picker__item-price-${item.index}`}
        >
          {item.monthlyRentalCost} p/m
        </Typography>
      </li>
    );
  };

  return isLoading ? (
    <div className="customise-options__content-loader">
      <Loader />
    </div>
  ) : !colourList ||
    colourList?.length == 0 ||
    getMatchedPrices(coloursPrices, colourList).filter((x) =>
      x.hasOwnProperty('hexCode')
    ).length == 0 ? (
    <OptionsList
      dataTestId="customise-options-content-colourpicker"
      availableOptions={coloursPrices}
      variant="colour"
    />
  ) : (
    <div
      className="colour-picker"
      data-testid="colour-picker"
      data-cy="colour-picker"
    >
      <ul ref={ref}>
        {vehicleColours.map((item, index) => (
          <ColorPickerItem key={uuid4()} {...item} index={index} />
        ))}
      </ul>
    </div>
  );
};

export default ColourPicker;
